import { Component, Input, OnInit } from '@angular/core';
import { DATA_TYPE } from '@constants/data-type.constant';
import { Signal } from '@models/dashboard/signal';
import { ChartSignalEntry } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedChartDetails } from '@models/pages/user-defined-page/user-defined-chart-details';
import {
  StatusEventSignalDataHelperService
} from '@services/status-event-signal-data/status-event-signal-data-helper.service';
import { TranslationService } from '@services/translation/translation.service';
import { UtilsService } from '@services/utils/utils.service';

@Component({
    selector: 'scitis-text-content',
    templateUrl: './text-content.component.html',
    styleUrls: ['./text-content.component.scss'],
    standalone: false
})
export class TextContentComponent implements OnInit {

  @Input()
  chartSettings: UserDefinedChartDetails | undefined;

  @Input()
  machineSignals: any = {};

  @Input()
  data: any = [];

  @Input()
  isInEditMode: boolean = false;
  private portalLanguage: string = '';

  private previousValuesMap: Map<number, any> = new Map<number, any>();

  constructor(
      private utilsService: UtilsService,
      private translationService: TranslationService,
      private statusEventSignalDataHelperService: StatusEventSignalDataHelperService) {
  }

  get showTextLiveWidget(): boolean {
    return !!this.chartSettings && !this.chartSettings.usingStaticData && this.chartSettings.signals && this.chartSettings.signals.length > 0;
  }

  get showTextStaticWidget(): boolean {
    return !!this.chartSettings && this.chartSettings.usingStaticData && this.chartSettings.staticData && this.chartSettings.staticData.length > 0;
  }

  ngOnInit(): void {
    this.portalLanguage = this.translationService.getActiveLanguage();
    this.translationService.getLanguageChangeEventSubject().subscribe((language: string) => {
      this.portalLanguage = language;
    });
  }

  getLabelAndUnit(signal: ChartSignalEntry): string {
    const label: string = signal.displayedText;
    let unit: string = this.getUnit(signal);
    if (!unit || unit.isNullOrWhitespace()) {
      unit = '';
    } else {
      unit = ' [' + unit + ']';
    }
    const machineSignalById = this.getMachineSignalById(signal.signalId, this.machineSignals);
    if (!machineSignalById) {
      return this.translationService.translate.instant(label) + unit;
    }
    return this.translationService.translate.instant(machineSignalById.label) + unit;
  }

  getValueOrFallback(signal: ChartSignalEntry): string {
    const machineSignalById = this.getMachineSignalById(signal.signalId, this.machineSignals);
    const value: string | undefined = this.getValue(signal, machineSignalById);
    if (!value && !this.previousValuesMap.get(signal.signalId)) {
      return 'N/A';
    }
    if (!value) {
      return this.previousValuesMap.get(signal.signalId) + '';
    }
    return value + '';
  }

  getStaticValue(staticDataProperty: string) {
    if (!this.data || this.data.length === 0 || this.isInEditMode) {
      return 'N/A';
    }

    return this.getMachineDataValue(staticDataProperty);
  }

  getValue(signal: ChartSignalEntry, machineSignalById: Signal | undefined): string | undefined {
    if (!this.isReceivedDataValid(this.data)) {
      return undefined;
    }
    let precision: number = signal.precision;
    let scale: number = signal.scale;

    if (machineSignalById) {
      precision = machineSignalById.decimals;
      scale = machineSignalById.scale;
    }

    const values = this.data[0];
    const filteredVs = values.vs.filter(m => m.s === String(signal.signalId));
    if (filteredVs && filteredVs.length > 0) {
      const value = filteredVs[0].v;

      const jsonObject = this.statusEventSignalDataHelperService.tryParseJSONObject(value);
      if (jsonObject != null) {
        const enhancedValue = this.statusEventSignalDataHelperService.getEnhancedValue(this.portalLanguage, jsonObject);
        this.previousValuesMap.set(signal.signalId, enhancedValue);
        return enhancedValue;
      }
      const parsedValue = this.parseSignalValue(signal.signalType, value, precision, scale);
      this.previousValuesMap.set(signal.signalId, parsedValue);
      return parsedValue;


    }

    return undefined;
  }

  getUnit(signal: ChartSignalEntry): string {
    let unit = '';
    if (signal.unit && signal.unit !== '-') {
      unit = signal.unit;
    }

    const machineSignalById = this.getMachineSignalById(signal.signalId, this.machineSignals);
    if (!machineSignalById) {
      return unit;
    }
    return machineSignalById.unit;
  }

  parseSignalValue(type, value, precision, scale: number) {
    return scale === 0 && (type === DATA_TYPE.INT16 || type === DATA_TYPE.INT32 || type === DATA_TYPE.INT64 || type === DATA_TYPE.INTEGER)
        ? Number(value).toLocaleString('fullwide', {useGrouping: false, maximumFractionDigits: 0})
        : this.utilsService.parseSignalValue(type, value, precision);
  }

  private getMachineDataValue(staticDataProperty: string): string {
    const map: Map<string, string> = this.data[0];
    let value;
    if (map.has(staticDataProperty)) {
      value = map.get(staticDataProperty);
    }
    if (!value) {
      return this.getMetaDataValue(staticDataProperty);
    }
    return value;
  }

  private getMetaDataValue(staticDataProperty: string): string {
    if (this.data.length === 1) {
      return 'N/A';
    }
    const metaMap: Map<string, string> = this.data[1];
    let value;
    if (metaMap.has(staticDataProperty)) {
      value = metaMap.get(staticDataProperty);
    }
    if (!value) {
      return 'N/A';
    }
    return value;
  }

  private isReceivedDataValid(data): boolean {
    if (!data || data.length === 0) {
      return false;
    }

    if (!data[0]?.vs || data[0].vs.length === 0) {
      return false;
    }

    return true;
  }

  private getMachineSignalById(id: number | string, machineSignals: Signal[]) {
    if (!machineSignals) {
      return undefined;
    }
    return Object.values(machineSignals).find(function (item: Signal) {
      return item.id === id;
    });

  }

}
